import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PlatformBreakdown {
  apple: number;
  google: number;
}

interface MemberGrowthData {
  date: string;
  count: number;
}

interface TransactionActivityData {
  date: string;
  earn_points: number;
  earn_stamps: number;
  redeem_points: number;
  redeem_stamps: number;
}

interface CustomerSegmentation {
  active: number;      // Last 30 days
  atRisk: number;      // 30-90 days
  inactive: number;    // 90+ days
  neverTransacted: number;
}

interface TopCustomer {
  customer_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  current_points: number;
  current_stamps: number;
  total_visits: number;
  last_transaction_date: string | null;
}

export interface LoyaltyStatistics {
  totalMembers: number;
  activePasses: number;
  platformBreakdown: PlatformBreakdown;
  totalStamps: number;
  totalPoints: number;
  totalRedemptions: number;
  memberGrowth: MemberGrowthData[];
  transactionActivity: TransactionActivityData[];
  customerSegmentation: CustomerSegmentation;
  topCustomers: TopCustomer[];
  averageVisits: number;
  activeRate: number;
  redemptionRate: number;
}

export const useLoyaltyStatistics = (businessId: string | null, programId?: string | null) => {
  const [stats, setStats] = useState<LoyaltyStatistics>({
    totalMembers: 0,
    activePasses: 0,
    platformBreakdown: { apple: 0, google: 0 },
    totalStamps: 0,
    totalPoints: 0,
    totalRedemptions: 0,
    memberGrowth: [],
    transactionActivity: [],
    customerSegmentation: { active: 0, atRisk: 0, inactive: 0, neverTransacted: 0 },
    topCustomers: [],
    averageVisits: 0,
    activeRate: 0,
    redemptionRate: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) return;

    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get loyalty program IDs
        let programIds: string[] = [];

        if (programId) {
          // Single program selected
          programIds = [programId];
        } else {
          // All programs for this business
          const { data: programs, error: programsError } = await supabase
            .from('loyalty_programs')
            .select('id')
            .eq('business_id', businessId)
            .is('deleted_at', null);

          if (programsError) throw programsError;
          programIds = programs?.map(p => p.id) || [];
        }

        if (programIds.length === 0) {
          // No programs, return empty stats
          setStats({
            totalMembers: 0,
            activePasses: 0,
            platformBreakdown: { apple: 0, google: 0 },
            totalStamps: 0,
            totalPoints: 0,
            totalRedemptions: 0,
            memberGrowth: [],
            transactionActivity: [],
            customerSegmentation: { active: 0, atRisk: 0, inactive: 0, neverTransacted: 0 },
            topCustomers: [],
            averageVisits: 0,
            activeRate: 0,
            redemptionRate: 0,
          });
          return;
        }

        // Fetch total members - count unique customers from passes (anyone with a pass is a member)
        const { data: passesForMemberCount, error: memberCountError } = await supabase
          .from('passes')
          .select('customer_id')
          .in('loyalty_program_id', programIds)
          .eq('status', 'active');

        if (memberCountError) throw memberCountError;

        // Count unique customer IDs
        const uniqueCustomerIds = new Set(passesForMemberCount?.map(p => p.customer_id) || []);
        const totalMembers = uniqueCustomerIds.size;

        // Fetch active passes with platform breakdown
        const { data: passesData, error: passesError } = await supabase
          .from('passes')
          .select('pass_type_identifier, pass_data')
          .in('loyalty_program_id', programIds)
          .eq('status', 'active');

        if (passesError) throw passesError;

        const activePasses = passesData?.length || 0;

        // Count platforms using same logic as LoyaltyProgramManager
        let appleCount = 0;
        let googleCount = 0;

        passesData?.forEach(pass => {
          const isGooglePass =
            pass.pass_type_identifier === 'pass.com.tag-innovation.google-wallet' ||
            (pass.pass_data && typeof pass.pass_data === 'object' && pass.pass_data.platform === 'google');

          if (isGooglePass) {
            googleCount++;
          } else {
            appleCount++;
          }
        });

        // Fetch transaction totals
        const { data: transactionsData, error: transactionsError } = await supabase
          .from('loyalty_transactions')
          .select('stamps_earned, points_earned, transaction_type')
          .in('program_id', programIds);

        if (transactionsError) throw transactionsError;

        const totalStamps = transactionsData?.reduce((sum, t) => sum + (t.stamps_earned || 0), 0) || 0;
        const totalPoints = transactionsData?.reduce((sum, t) => sum + (t.points_earned || 0), 0) || 0;
        const totalRedemptions = transactionsData?.filter(t =>
          t.transaction_type === 'redeem_points' || t.transaction_type === 'redeem_stamps'
        ).length || 0;

        // Calculate redemption rate
        const totalEarnings = transactionsData?.filter(t =>
          t.transaction_type === 'earn_points' || t.transaction_type === 'earn_stamps'
        ).length || 0;
        const redemptionRate = totalEarnings > 0 ? (totalRedemptions / totalEarnings) * 100 : 0;

        // Fetch member growth (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data: growthData, error: growthError } = await supabase
          .from('passes')
          .select('created_at')
          .in('loyalty_program_id', programIds)
          .gte('created_at', thirtyDaysAgo.toISOString())
          .order('created_at', { ascending: true });

        if (growthError) throw growthError;

        // Group by date
        const growthByDate: { [key: string]: number } = {};
        growthData?.forEach(pass => {
          const date = new Date(pass.created_at).toISOString().split('T')[0];
          growthByDate[date] = (growthByDate[date] || 0) + 1;
        });

        const memberGrowth = Object.entries(growthByDate).map(([date, count]) => ({
          date,
          count,
        }));

        // Fetch transaction activity (last 30 days)
        const { data: activityData, error: activityError } = await supabase
          .from('loyalty_transactions')
          .select('created_at, transaction_type, stamps_earned, points_earned, stamps_redeemed, points_redeemed')
          .in('program_id', programIds)
          .gte('created_at', thirtyDaysAgo.toISOString())
          .order('created_at', { ascending: true });

        if (activityError) throw activityError;

        // Group by date and transaction type
        const activityByDate: { [key: string]: TransactionActivityData } = {};
        activityData?.forEach(txn => {
          const date = new Date(txn.created_at).toISOString().split('T')[0];
          if (!activityByDate[date]) {
            activityByDate[date] = {
              date,
              earn_points: 0,
              earn_stamps: 0,
              redeem_points: 0,
              redeem_stamps: 0,
            };
          }

          // Transaction types are 'earn', 'redeem', or 'manual_adjustment'
          // Earn: use points_earned and stamps_earned
          // Redeem: use points_redeemed and stamps_redeemed
          if (txn.transaction_type === 'earn') {
            activityByDate[date].earn_points += txn.points_earned || 0;
            activityByDate[date].earn_stamps += txn.stamps_earned || 0;
          } else if (txn.transaction_type === 'redeem') {
            activityByDate[date].redeem_points += txn.points_redeemed || 0;
            activityByDate[date].redeem_stamps += txn.stamps_redeemed || 0;
          }
        });

        const transactionActivity = Object.values(activityByDate);

        // Fetch customer segmentation - need to consider ALL customers (with passes), not just those with balances
        // First get all unique customers from passes
        const allCustomerIds = Array.from(uniqueCustomerIds);

        // Fetch balances for these customers
        const { data: balancesData, error: balancesError } = await supabase
          .from('customer_loyalty_balances')
          .select('customer_id, last_transaction_date, total_visits')
          .in('program_id', programIds);

        if (balancesError) throw balancesError;

        // Create a map of customer balances
        const balancesByCustomer = new Map();
        balancesData?.forEach(balance => {
          balancesByCustomer.set(balance.customer_id, balance);
        });

        const now = new Date();
        let active = 0, atRisk = 0, inactive = 0, neverTransacted = 0;
        let totalVisits = 0;

        // Iterate through all customers (from passes)
        allCustomerIds.forEach(customerId => {
          const balance = balancesByCustomer.get(customerId);

          if (!balance || !balance.last_transaction_date) {
            neverTransacted++;
          } else {
            totalVisits += balance.total_visits || 0;

            const daysSinceTransaction = Math.floor(
              (now.getTime() - new Date(balance.last_transaction_date).getTime()) / (1000 * 60 * 60 * 24)
            );

            if (daysSinceTransaction <= 30) active++;
            else if (daysSinceTransaction <= 90) atRisk++;
            else inactive++;
          }
        });

        const customerSegmentation = { active, atRisk, inactive, neverTransacted };
        const averageVisits = totalMembers > 0
          ? totalVisits / totalMembers
          : 0;
        const activeRate = totalMembers > 0 ? (active / totalMembers) * 100 : 0;

        // Fetch top customers (balances only first)
        const { data: topCustomersData, error: topCustomersError } = await supabase
          .from('customer_loyalty_balances')
          .select('customer_id, current_points, current_stamps, total_visits, last_transaction_date')
          .eq('business_id', businessId)
          .order('total_visits', { ascending: false })
          .limit(10);

        if (topCustomersError) throw topCustomersError;

        // Extract customer IDs (text) and fetch pass data to get customer info
        const customerIds = (topCustomersData || []).map(b => b.customer_id);

        // Fetch passes with pass_data to extract customer information
        const customerInfoMap: Map<string, any> = new Map();

        if (customerIds.length > 0) {
          const { data: passesData } = await supabase
            .from('passes')
            .select('customer_id, customer_uuid, pass_data')
            .in('customer_id', customerIds);

          if (passesData) {
            // Group by customer_id (take first pass for each customer)
            const passesByCustomer = new Map();
            passesData.forEach(pass => {
              if (!passesByCustomer.has(pass.customer_id)) {
                passesByCustomer.set(pass.customer_id, pass);
              }
            });

            // Extract customer UUIDs for customers table lookup
            const customerUuids = Array.from(passesByCustomer.values())
              .map(p => p.customer_uuid)
              .filter((uuid): uuid is string => uuid !== null);

            // Fetch from customers table if we have UUIDs
            const customersTableData = new Map();
            if (customerUuids.length > 0) {
              const { data: customersData } = await supabase
                .from('customers')
                .select('id, email, first_name, last_name, phone')
                .in('id', customerUuids);

              if (customersData) {
                customersData.forEach(customer => {
                  customersTableData.set(customer.id, customer);
                });
              }
            }

            // Build customer info map from passes and customers table
            passesByCustomer.forEach((pass, customerId) => {
              let customerInfo: any = {
                email: null,
                first_name: null,
                last_name: null,
                phone: null
              };

              // First, try to get from customers table if UUID exists
              if (pass.customer_uuid && customersTableData.has(pass.customer_uuid)) {
                const tableData = customersTableData.get(pass.customer_uuid);
                customerInfo = { ...tableData };
              }

              // If still no data, extract from pass_data JSON
              if (!customerInfo.first_name && !customerInfo.last_name && pass.pass_data) {
                try {
                  const passData = typeof pass.pass_data === 'string'
                    ? JSON.parse(pass.pass_data)
                    : pass.pass_data;

                  // Check for accountName in Google Wallet passes
                  if (passData.loyaltyObject?.accountName) {
                    customerInfo.first_name = passData.loyaltyObject.accountName;
                  }

                  // Check textModulesData for name field
                  if (!customerInfo.first_name && passData.loyaltyObject?.textModulesData) {
                    const nameModule = passData.loyaltyObject.textModulesData.find(
                      (m: any) => m.id === 'name' || m.header === 'MEMBER'
                    );
                    if (nameModule?.body) {
                      customerInfo.first_name = nameModule.body;
                    }
                  }

                  // Check for email in pass data
                  if (passData.loyaltyObject?.accountId?.includes('@')) {
                    customerInfo.email = passData.loyaltyObject.accountId;
                  }
                } catch (error) {
                  console.warn('Error parsing pass_data:', error);
                }
              }

              // If customer_id looks like an email, use it as fallback
              if (!customerInfo.email && customerId.includes('@')) {
                customerInfo.email = customerId;
              }

              customerInfoMap.set(customerId, customerInfo);
            });
          }
        }

        // Map the data with customer details
        const topCustomersWithDetails: TopCustomer[] = (topCustomersData || []).map(balance => {
          const customerInfo = customerInfoMap.get(balance.customer_id) || {
            email: null,
            first_name: null,
            last_name: null,
            phone: null
          };

          return {
            customer_id: balance.customer_id,
            email: customerInfo.email,
            first_name: customerInfo.first_name,
            last_name: customerInfo.last_name,
            phone: customerInfo.phone,
            current_points: balance.current_points || 0,
            current_stamps: balance.current_stamps || 0,
            total_visits: balance.total_visits || 0,
            last_transaction_date: balance.last_transaction_date,
          };
        });

        setStats({
          totalMembers,
          activePasses,
          platformBreakdown: { apple: appleCount, google: googleCount },
          totalStamps,
          totalPoints,
          totalRedemptions,
          memberGrowth,
          transactionActivity,
          customerSegmentation,
          topCustomers: topCustomersWithDetails,
          averageVisits: Math.round(averageVisits * 10) / 10,
          activeRate: Math.round(activeRate * 10) / 10,
          redemptionRate: Math.round(redemptionRate * 10) / 10,
        });
      } catch (err) {
        console.error('Error fetching loyalty statistics:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [businessId, programId]);

  return { stats, loading, error };
};
